// Singleton audio engine for the floating ambient player.
//
// Hosted styles ('piano', 'nature') stream a single looped MP3 from
// the ambient-audio Supabase Storage bucket. If the file isn't found or the
// browser blocks autoplay, we fall back to a Web Audio synth drone so the
// player always has *something* to play once the user clicks.

const SUPABASE_URL =
  (import.meta as any).env?.VITE_SUPABASE_URL ??
  'https://ifkrklrmsjszdloohxvx.supabase.co';

export type AmbienceStyle = 'spa' | 'piano' | 'nature' | 'forest' | 'vivaldi' | 'synth' | 'silence';

export const STYLE_META: Record<AmbienceStyle, { label: string; trackName: string }> = {
  spa:     { label: 'Spa music',        trackName: 'Spa & relaxation'    },  // YouTube-backed
  piano:   { label: 'Soft piano',       trackName: 'Piano drift'         },
  nature:  { label: 'Ocean waves',      trackName: 'Ocean at dusk'       },  // mp3 is ocean sounds
  forest:  { label: 'Forest sounds',    trackName: 'Forest & birdsong'   },  // YouTube-backed
  vivaldi: { label: 'Vivaldi',          trackName: 'The Four Seasons'    },  // YouTube-backed
  synth:   { label: 'Web Audio drone',  trackName: 'Slow drone'          },
  silence: { label: 'Silence',          trackName: 'Silence'             },
};

// YouTube video IDs for styles that stream from YouTube instead of Supabase Storage.
// spa     → "3 Hours of Relaxing Spa Music" by Soothing Relaxation
//            Verify/swap this ID at youtube.com if you want a different video.
// forest  → "Forest Birds – 8 Hour Ambient Soundscape" (2014, relaxdaily)
// vivaldi → "Vivaldi Four Seasons – Voices of Music on original instruments" (4K)
export const YOUTUBE_BACKED: Partial<Record<AmbienceStyle, string>> = {
  spa:     'APFAGaSVMiM',  // Soothing Relaxation — swap if you prefer a different video
  forest:  'vgqQSVFch44',
  vivaldi: 'aryDMAP6oug',
};

export function isYouTubeStyle(style: AmbienceStyle): boolean {
  return style in YOUTUBE_BACKED;
}

const STYLE_FILES: Partial<Record<AmbienceStyle, string>> = {
  piano:  'piano-loop.mp3',
  nature: 'nature-loop.mp3',
};

function publicAudioUrl(file: string): string {
  return `${SUPABASE_URL}/storage/v1/object/public/ambient-audio/${file}`;
}

interface SynthHandle {
  ctx: AudioContext;
  master: GainNode;
}

interface AmbienceSnapshot {
  style: AmbienceStyle;
  isPlaying: boolean;
  volume: number;
}

class AmbienceEngine {
  private audio: HTMLAudioElement | null = null;
  private synth: SynthHandle | null = null;
  private currentStyle: AmbienceStyle = 'silence';
  private isPlaying = false;
  private volume = 0.3;
  private listeners = new Set<() => void>();

  // Cached snapshot reference. useSyncExternalStore requires that getSnapshot
  // returns the SAME object reference whenever the underlying data is
  // unchanged — if every call produces a new {...} literal React thinks the
  // store changed on every render and re-renders forever, triggering
  // React error #185 (Maximum update depth exceeded). emit() rebuilds the
  // snapshot exactly once per real state change.
  private snapshot: AmbienceSnapshot = {
    style: this.currentStyle,
    isPlaying: this.isPlaying,
    volume: this.volume,
  };

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  private emit() {
    this.snapshot = {
      style: this.currentStyle,
      isPlaying: this.isPlaying,
      volume: this.volume,
    };
    for (const fn of this.listeners) fn();
  }

  getState(): AmbienceSnapshot {
    return this.snapshot;
  }

  setVolume(v: number) {
    this.volume = Math.max(0, Math.min(1, v));
    if (this.audio) this.audio.volume = this.volume;
    if (this.synth) {
      this.synth.master.gain.setTargetAtTime(this.volume * 0.25, this.synth.ctx.currentTime, 0.05);
    }
    this.emit();
  }

  // Plays the requested style. Returns true if audio actually started, false
  // if the browser blocked autoplay (caller can retry on next user gesture).
  async play(style: AmbienceStyle): Promise<boolean> {
    this.stopInternal();
    this.currentStyle = style;

    if (style === 'silence') {
      this.isPlaying = false;
      this.emit();
      return true;
    }

    // YouTube-backed styles: the React component owns the YT player lifecycle.
    // The engine just tracks playing state; no audio work needed here.
    if (isYouTubeStyle(style)) {
      this.isPlaying = true;
      this.emit();
      return true;
    }

    if (style === 'synth') {
      try {
        this.startSynth();
        this.isPlaying = true;
        this.emit();
        return true;
      } catch {
        this.isPlaying = false;
        this.emit();
        return false;
      }
    }

    const file = STYLE_FILES[style];
    if (!file) {
      this.isPlaying = false;
      this.emit();
      return false;
    }

    const audio = new Audio(publicAudioUrl(file));
    audio.loop = true;
    audio.volume = this.volume;
    audio.crossOrigin = 'anonymous';

    try {
      await audio.play();
      this.audio = audio;
      this.isPlaying = true;
      this.emit();
      return true;
    } catch (err) {
      // Two paths land here: autoplay blocked, or 404 if the file isn't
      // uploaded yet. In both cases we fall back to the synth so the user
      // hears *something* and can iterate.
      console.warn('Ambience audio failed, falling back to synth:', err);
      try {
        this.startSynth();
        this.isPlaying = true;
        this.currentStyle = 'synth';
        this.emit();
        return true;
      } catch {
        this.isPlaying = false;
        this.emit();
        return false;
      }
    }
  }

  pause() {
    this.stopInternal();
    this.isPlaying = false;
    this.emit();
  }

  private stopInternal() {
    if (this.audio) {
      try { this.audio.pause(); } catch {}
      this.audio = null;
    }
    if (this.synth) {
      try { this.synth.ctx.close(); } catch {}
      this.synth = null;
    }
  }

  private startSynth() {
    this.startSynthForStyle(this.currentStyle);
  }

  private startSynthForStyle(style: AmbienceStyle) {
    const Ctor = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!Ctor) throw new Error('Web Audio API not available');
    const ctx: AudioContext = new Ctor();

    const master = ctx.createGain();
    master.gain.value = this.volume * 0.25;
    master.connect(ctx.destination);

    if (style === 'nature') {
      // Pink-ish noise through a bandpass filter — rain/forest feel
      const bufferSize = ctx.sampleRate * 4;
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const data = buffer.getChannelData(0);
      let b0 = 0, b1 = 0, b2 = 0, b3 = 0, b4 = 0, b5 = 0;
      for (let i = 0; i < bufferSize; i++) {
        const white = Math.random() * 2 - 1;
        b0 = 0.99886 * b0 + white * 0.0555179;
        b1 = 0.99332 * b1 + white * 0.0750759;
        b2 = 0.96900 * b2 + white * 0.1538520;
        b3 = 0.86650 * b3 + white * 0.3104856;
        b4 = 0.55000 * b4 + white * 0.5329522;
        b5 = -0.7616 * b5 - white * 0.0168980;
        data[i] = (b0 + b1 + b2 + b3 + b4 + b5 + white * 0.5362) * 0.11;
      }
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.loop = true;

      // Low-pass filter to soften harsh highs
      const lpf = ctx.createBiquadFilter();
      lpf.type = 'lowpass';
      lpf.frequency.value = 1200;
      lpf.Q.value = 0.5;

      // Slow tremolo for wind-through-leaves feel
      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = 0.12;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = this.volume * 0.06;
      lfo.connect(lfoGain);
      lfoGain.connect(master.gain);
      lfo.start();

      source.connect(lpf);
      lpf.connect(master);
      source.start();
    } else if (style === 'piano') {
      // Bell-like tones: sine waves with fast decay, cycling slowly
      const notes = [261.63, 329.63, 392.00, 523.25, 659.25]; // C4, E4, G4, C5, E5
      let noteIndex = 0;

      const playNote = () => {
        if (!this.synth) return;
        const freq = notes[noteIndex % notes.length];
        noteIndex++;

        const osc = ctx.createOscillator();
        osc.type = 'sine';
        osc.frequency.value = freq;

        const env = ctx.createGain();
        env.gain.setValueAtTime(0, ctx.currentTime);
        env.gain.linearRampToValueAtTime(this.volume * 0.3, ctx.currentTime + 0.02);
        env.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 3.5);

        osc.connect(env);
        env.connect(master);
        osc.start();
        osc.stop(ctx.currentTime + 3.6);
      };

      // Play a note, then schedule the next one
      const scheduleNext = () => {
        if (!this.synth) return;
        playNote();
        const delay = 1800 + Math.random() * 2400; // 1.8 – 4.2 s between notes
        setTimeout(scheduleNext, delay);
      };
      setTimeout(scheduleNext, 300);
    } else {
      // Default synth drone: two oscillators a perfect fifth apart
      const fundamental = 110; // A2
      const fifth = fundamental * 1.5;

      const osc1 = ctx.createOscillator();
      osc1.type = 'sine';
      osc1.frequency.value = fundamental;
      osc1.detune.value = -3;
      osc1.connect(master);
      osc1.start();

      const osc2 = ctx.createOscillator();
      osc2.type = 'sine';
      osc2.frequency.value = fifth;
      osc2.detune.value = 5;
      osc2.connect(master);
      osc2.start();

      // Slow LFO breathing
      const lfo = ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = 0.05;
      const lfoGain = ctx.createGain();
      lfoGain.gain.value = this.volume * 0.08;
      lfo.connect(lfoGain);
      lfoGain.connect(master.gain);
      lfo.start();
    }

    this.synth = { ctx, master };
  }
}

let _engine: AmbienceEngine | null = null;
export function getAmbienceEngine(): AmbienceEngine {
  if (typeof window === 'undefined') {
    throw new Error('Ambience engine only runs in the browser');
  }
  if (!_engine) _engine = new AmbienceEngine();
  return _engine;
}
